import * as THREE from "three";
import { World } from "./World";
import {
    vSkyNightShader, fSkyNightShader,
    vCloudsShader, fCloudsShader,
    vDefaultShader, fBasicHatchingShader,
} from '../shaders/';

import { ParticlesSystem } from "../components/ParticlesSystem";

export class NightWorld extends World {
    constructor({ name, model, assets }) {
        super({ name, model, assets })
        this.camera.near = .1
        this.camera.far = 100
        this.titleBrightness = 1
        this.titleDisplace = -.25
        this.titleScale = .9
        this.createParticlesSystems()
    }

    processModel() {
        super.processModel();

        this.sky = this.model.scene.getObjectByName('sky')
        this.mapSky = this.model.scene.getObjectByName('sky').material.map

        this.sky.material = new THREE.ShaderMaterial({
            uniforms: {
                map: { value: this.mapSky },
                noiseMap: { value: this.assets.textures.noise },
                hatchingScale: { value: 10 },
                hatchingMap: { value: this.assets.textures.hatchC },
                mixDiffuse: { value: 0 },
                gamma: { value: 2.2 },
                whiteLevel: { value: 1.4 },
                blackLevel: { value: -.1 },
                time: { value: 0 },
            },
            vertexShader: vSkyNightShader,
            fragmentShader: fSkyNightShader,
        })

        const basicMat = new THREE.ShaderMaterial({
            uniforms: {
                map: { value: null },
                hatchingScale: { value: 20 },
                hatchingMap: { value: this.assets.textures.hatchC },
                gamma: { value: 2 },
                mixDiffuse: { value: .6 },
                whiteLevel: { value: 1 },
                blackLevel: { value: 0 },
                time: { value: 0 },
            },
            vertexShader: vDefaultShader,
            fragmentShader: fBasicHatchingShader,
        })

        // MOUNTAIN
        this.mountain = this.model.scene.getObjectByName('mountain')
        const mountainMat = basicMat.clone()
        mountainMat.uniforms.map.value = this.mountain.material.map
        mountainMat.uniforms.hatchingScale.value = 10
        mountainMat.uniforms.gamma.value = 1.7
        mountainMat.uniforms.whiteLevel.value = 1.3
        mountainMat.uniforms.blackLevel.value = -.05

        this.mountain.material = mountainMat

        // MONSTER

        this.monster = this.model.scene.getObjectByName('monster')
        const monsterMat = basicMat.clone()
        monsterMat.uniforms.map.value = this.monster.material.map
        monsterMat.uniforms.hatchingScale.value = 10
        monsterMat.uniforms.gamma.value = 1.9
        monsterMat.uniforms.blackLevel.value = -.02
        //monsterMat.uniforms.whiteLevel.value = 1.02
        this.monster.material = monsterMat

        // BUILDINGS

        this.buildings = this.model.scene.getObjectByName('buildings')
        const buildingMat = basicMat.clone()
        buildingMat.uniforms.map.value = this.buildings.material.map
        buildingMat.uniforms.hatchingScale.value = 5
        buildingMat.uniforms.gamma.value = 1.6
        buildingMat.uniforms.whiteLevel.value = 1.1
        buildingMat.uniforms.blackLevel.value = -.1
        this.buildings.material = buildingMat

        // CHARACTERS

        this.king = this.model.scene.getObjectByName('king')
        this.queen = this.model.scene.getObjectByName('queen')
        this.rabbits = this.model.scene.getObjectByName('rabbits')

        const kingMat = basicMat.clone()
        kingMat.uniforms.map.value = this.king.material.map
        kingMat.uniforms.hatchingScale.value = 30
        this.king.material = kingMat
        this.queen.material = kingMat
        this.rabbits.material = kingMat


        // DECO
        this.deco = this.model.scene.getObjectByName('deco')

        const decoMat = basicMat.clone()
        decoMat.uniforms.map.value = this.deco.material.map
        decoMat.uniforms.hatchingScale.value = 10
        this.deco.material = decoMat


        // SOCLE

        this.socle = this.model.scene.getObjectByName('socle')

        const socleMat = basicMat.clone()
        socleMat.uniforms.map.value = this.socle.material.map
        socleMat.uniforms.hatchingScale.value = 30
        this.socle.material = socleMat

        // CLOUDS

        this.mapClouds = this.model.scene.getObjectByName('cloud').material.map
        this.matClouds = new THREE.ShaderMaterial({
            uniforms: {
                map: { value: this.mapClouds },
                hatchingScale: { value: 10 },
                hatchingMap: { value: this.assets.textures.hatchC },
                mixDiffuse: { value: 0 },
                gamma: { value: 1.5 },
                blackLevel: { value: 0 },
                whiteLevel: { value: 1 },
                time: { value: 0 },
            },
            vertexShader: vCloudsShader,
            fragmentShader: fCloudsShader,
            side: THREE.DoubleSide,
            transparent: true,
        })

        // LEAF
        this.leaf = this.model.scene.getObjectByName('leaf')

        this.emitters = []

        this.model.scene.traverse(mesh => {
            if (mesh.name.indexOf("cloud") > -1) {
                mesh.material = this.matClouds
            }

            else if (mesh.name.indexOf("foliageEmitter") > -1) {
                this.emitters.push(mesh);
            }
        })

    }

    createParticlesSystems() {
        this.particlesSystems = [];
        for (let i = 0; i < this.emitters.length; i++) {
            const ps = new ParticlesSystem({
                scene: this.scene,
                emitter: this.emitters[i],
                particle: this.leaf,
                count: 50,
                particleLife: 5,
                particleMaxDelay: 3,
                particleScale: new THREE.Vector3(3, 3, 3),
                particleScaleVariation: new THREE.Vector3(1, 1, 1),
                particleScaleSpeed: new THREE.Vector3(-1, -1, -1),
                particleScaleSpeedVariation: new THREE.Vector3(.1, .1, .1),
                followNormal: true,
                normalVariation: new THREE.Vector3(0, 0, 0),
                particleTransSpeed: .5,
                particleTransVariation: .02,
                particleRotSpeed: new THREE.Vector3(2, .1, .1),
            })
            ps.createParticles()
            this.particlesSystems.push(ps);
        }

    }

    loop({ time, deltaTime }) {
        super.loop({ time, deltaTime });

        if (this.sky) {
            this.sky.material.uniforms.time.value = time;
        }

        if (this.matClouds) {
            this.matClouds.uniforms.time.value = time;
        }

        this.particlesSystems.forEach(ps => ps.updateParticles(deltaTime));
    }
}